import axios from "axios";
import router from "../router";
import Vue from "vue";
import { ensureToken, clearToken, acquireToken, readToken } from "./cloudToken";

export default (server)  => {
    const axiosInstance = axios.create({
        baseURL: `${server.server_api}/api`,
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    Vue.prototype.$http = axiosInstance;
    Vue.config.productionTip = false;
    axiosInstance.defaults.timeout = 1000000;

    // CL-18307: the interceptor is async and AWAITS the token.
    //
    // It used to read localStorage synchronously and send the request without
    // an Authorization header when there was nothing there, which meant the
    // first call to a regional API in a session was guaranteed to 401 and the
    // answer the user was waiting for never arrived. On the Add Company
    // subdomain field, where a request went out per keystroke, that turned
    // into a wall of 401s plus two token calls behind each one.
    //
    // Returning a promise from a request interceptor is supported by axios and
    // simply delays dispatch, so the ordering is now: token first, request
    // second. `ensureToken` resolves null rather than rejecting if acquisition
    // fails, so a token outage degrades to the pre-existing 401 path instead of
    // throwing from inside the interceptor.
    axiosInstance.interceptors.request.use(
        async (config) => {
            const cloudAccesstoken = await ensureToken(server);

            if (!config.headers) {
                config.headers = {};
            }

            // Set the header DIRECTLY rather than under `headers.common`, and
            // clear both spellings first.
            //
            // axios flattens headers as merge(common, <method>, headers) with
            // `headers` last, so a direct key beats a `common` one. On the
            // first dispatch that distinction is invisible -- there is no
            // direct key yet -- but the 401 retry below re-sends the config
            // from the failed attempt, whose headers have ALREADY been
            // flattened and therefore carry the rejected token as a direct
            // key. Writing only to `common` there would be silently
            // overridden by the dead token and the retry would 401 too.
            delete config.headers["Authorization"];
            if (config.headers.common) {
                delete config.headers.common["Authorization"];
            }

            if (cloudAccesstoken) {
                config.headers["Authorization"] = `Bearer ${cloudAccesstoken}`;
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
    axiosInstance.interceptors.response.use(
        (response) => {
            if (response.status === 200 || response.status === 201) {
                return Promise.resolve(response);
            } else {
              return Promise.reject(response);
            }
        },
        async (error)  => {
            if (error && error.response && error.response.status) {
                switch (error.response.status) {
                    case 429: // Too many requests
                        break;
                    case 401:{
                        // The stored token is the one that was just rejected,
                        // so drop it before acquiring: otherwise `ensureToken`
                        // on the retry below would happily hand back the same
                        // dead token and the retry would 401 as well.
                        clearToken(server);

                        // Single-flight; also writes the new token, and writes
                        // nothing (rather than the string "undefined", which
                        // used to poison the key and make every later read
                        // throw from JSON.parse) when acquisition fails.
                        const refreshed = await acquireToken(server);

                        // Retry the original request ONCE. The old code
                        // refreshed the token and then dropped the request on
                        // the floor, so a token that expired mid-session
                        // surfaced as a failed check even though the very next
                        // identical request would have succeeded. Callers
                        // papered over that with their own retries -- the Vuex
                        // subdomain action recursed on 401 with no bound at
                        // all, which is part of this ticket's request
                        // multiplication.
                        //
                        // `__isTokenRetry` makes it strictly one attempt: a
                        // retry that 401s again lands here with the flag set
                        // and falls through to the rejection below.
                        if (refreshed && error.config && !error.config.__isTokenRetry) {
                            error.config.__isTokenRetry = true;
                            return axiosInstance.request(error.config);
                        }
                        break;
                    }
                    case 403:
                        router.replace({
                            path: "/login",
                            query: { redirect: router.currentRoute.fullPath },
                        });
                        break;
                    case 404:
                        alert("page not exist");
                        break;
                    case 502:
                        setTimeout(() => {
                            router.replace({
                                path: "/login",
                            });
                        }, 1000);
                        break;
                    default:
                        "It does not have HTTP ok status"
                }
            }
            return Promise.reject(error);
        }
    );
    return axiosInstance;
};

// Re-exported so callers that only need to know whether a token exists do not
// have to reach into the storage layout themselves.
export { readToken };
